import type { GeometryPayload } from '../types/mathGeometry';

type MeasureType = 'perimeter' | 'area' | 'circumference';

function normalizeText(text: string): string {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function parseDim(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function detectGeometryMeasureType(text: string): MeasureType | null {
  const t = normalizeText(text);
  if (/perimetro/.test(t)) return 'perimeter';
  if (/area|superficie/.test(t)) return 'area';
  if (/circunferencia|contorno|longitud.*contorno/.test(t)) return 'circumference';
  return null;
}

export function computeGeometryMeasure(
  geometry: GeometryPayload | undefined,
  measureType: MeasureType,
): number | null {
  if (!geometry?.shapeType) return null;
  const shape = geometry.shapeType.toLowerCase();
  const d = geometry.dimensions ?? {};

  if (shape === 'rectangle') {
    const w = parseDim(d.width);
    const h = parseDim(d.height);
    if (w == null || h == null) return null;
    if (measureType === 'perimeter') return 2 * (w + h);
    if (measureType === 'area') return w * h;
  }

  if (shape === 'triangle') {
    const base = parseDim(d.base ?? d.width);
    const height = parseDim(d.height);
    if (base == null || height == null) return null;
    if (measureType === 'area') return (base * height) / 2;
  }

  if (shape === 'circle') {
    const r = parseDim(d.radius);
    if (r == null) return null;
    if (measureType === 'circumference') return Math.round(2 * Math.PI * r * 10) / 10;
    if (measureType === 'area') return Math.round(Math.PI * r * r * 10) / 10;
  }

  return null;
}

function formatMeasureValue(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 10) / 10).replace('.', ',');
}

export function repairCompletarGeometryNumericAnswers(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const geometry = payload.geometry as GeometryPayload | undefined;
  if (!geometry?.shapeType) return payload;

  const text = String(payload.text || '');
  if (!/\[[^\]]*\]/.test(text)) return payload;

  const measureType = detectGeometryMeasureType(text);
  if (!measureType) return payload;

  const expected = computeGeometryMeasure(geometry, measureType);
  if (expected == null) return payload;

  const formatted = formatMeasureValue(expected);
  let changed = false;

  const nextText = text.replace(/\[([^\]]*)\]/g, (full, inner) => {
    const token = String(inner || '').trim();
    if (!token || /^blank$/i.test(token) || token === ' ') {
      changed = true;
      return `[${formatted}]`;
    }
    const numeric = Number(token.replace(',', '.'));
    if (Number.isFinite(numeric) && Math.abs(numeric - expected) > 0.6) {
      changed = true;
      return `[${formatted}]`;
    }
    return full;
  });

  if (!changed) return payload;
  return { ...payload, text: nextText };
}
